import styled, { css } from 'styled-components'
import {
  StyledNumericFilterProps,
  StyledInputGroupProps,
  StyledStepperProps,
  StyledPresetButtonProps,
  StyledActionsProps,
} from './NumericFilter.types'

export const FilterContainer = styled.div<StyledNumericFilterProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.default};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme, $compact }) =>
    $compact ? theme.spacing.default : theme.spacing.medium};
  box-shadow: ${({ theme }) => theme.shadows.default};
  min-width: 280px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    min-width: 260px;
    padding: ${({ theme }) => theme.spacing.default};
  }
`

export const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

export const InputGroup = styled.div<StyledInputGroupProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.default};
`

export const InputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
`

export const PrefixSuffix = styled.span`
  position: absolute;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  pointer-events: none;
  white-space: nowrap;
`

export const Prefix = styled(PrefixSuffix)`
  left: ${({ theme }) => theme.spacing.default};
`

export const Suffix = styled(PrefixSuffix)`
  right: ${({ theme }) => theme.spacing.default};
`

export const StyledInput = styled.input<{
  $hasPrefix: boolean
  $hasSuffix: boolean
}>`
  width: 100%;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: 0 ${({ theme }) => theme.spacing.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $hasPrefix, theme }) =>
    $hasPrefix &&
    css`
      padding-left: calc(${theme.spacing.default} * 2 + 20px);
    `}

  ${({ $hasSuffix, theme }) =>
    $hasSuffix &&
    css`
      padding-right: calc(${theme.spacing.default} * 2 + 20px);
    `}

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.tertiary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const Separator = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  padding: 0 ${({ theme }) => theme.spacing.micro};
`

export const StepperButton = styled.button<StyledStepperProps>`
  width: 24px;
  height: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  font-size: 12px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.border.focus};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:active:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
    `}
`

export const PresetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

export const PresetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing.small};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`

export const PresetButton = styled.button<StyledPresetButtonProps>`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primaryLight : theme.colors.background.primary};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme, $active }) =>
    $active
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-align: center;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primaryLight : theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.primary};
    `}
`

export const ActionsContainer = styled.div<StyledActionsProps>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  margin-top: ${({ theme, $compact }) =>
    $compact ? theme.spacing.small : theme.spacing.default};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`

export const ActionButton = styled.button<{
  $variant: 'primary' | 'secondary'
}>`
  flex: 1;
  height: 32px;
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === 'primary'
        ? theme.colors.primary
        : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme, $variant }) =>
    $variant === 'primary'
      ? theme.colors.primary
      : theme.colors.background.primary};
  color: ${({ theme, $variant }) =>
    $variant === 'primary'
      ? theme.colors.text.inverse
      : theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme, $variant }) =>
      $variant === 'primary'
        ? theme.colors.primaryHover
        : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
